<template>
  <div v-if="visible" class="fixed top-0 left-0 w-full h-full bg-surface-600 z-[200] dark:bg-surface-900 flex items-center justify-center">
  <Dialog 
    :visible="visible"
    @update:visible="handleClose"
    :modal="true"
    :closable="false"
    :draggable="false"
    class="api-setup-modal"
    :style="{ width: '650px' }"
  >
    <!-- Header personalizzato -->
    <template #header>
      <div class="flex items-center gap-4">
        <div class="w-12 h-12 flex items-center justify-center bg-momap/10 dark:bg-momap/5">
          <span class="material-symbols-outlined text-3xl text-momap">api</span>
        </div>
        <div>
          <h2 class="text-xl font-bold">Configurazione API</h2>
          <p class="text-sm text-surface-500 dark:text-surface-400">Completa la configurazione per iniziare</p>
        </div>
      </div>
    </template>

    <!-- Contenuto principale -->
    <div class="relative">
    

      <!-- Step content -->
      <div class="min-h-[400px] flex flex-col">
        <Transition name="fade-slide">
          <!-- Welcome step -->
          <div v-if="currentStep === 0" key="welcome" class="animate-fadeIn flex-1 flex items-center">
            <div class="text-center space-y-6 w-full">
              <h3 class="text-2xl font-semibold">Benvenuto nella configurazione API</h3>
              <p class="text-surface-600 dark:text-surface-400 max-w-md mx-auto">
                Scegli se vuoi generare nuove credenziali API o inserire quelle esistenti.
              </p>
              <div class="flex justify-center gap-6 pt-4">
                <Button 
                  class="!px-6 !bg-green-500 hover:!bg-green-600"
                  @click="setupMode = 'generate'; currentStep++"
                >
                  <div class="flex items-center gap-3">
                    <span class="material-symbols-outlined">add_circle</span>
                    <span>Genera Nuove</span>
                  </div>
                </Button>
                <Button 
                  class="!px-6 !bg-blue-500 hover:!bg-blue-600"
                  @click="setupMode = 'manual'; currentStep = 3"
                >
                  <div class="flex items-center gap-3">
                    <span class="material-symbols-outlined">key</span>
                    <span>Inserisci Esistenti</span>
                  </div>
                </Button>
              </div>
            </div>
          </div>

          <!-- Permissions step -->
          <div v-else-if="currentStep === 1" key="permissions" class="animate-fadeIn">
            <div class="space-y-6">
              <!-- Admin switch -->
              <div class="p-4 bg-surface-50 dark:bg-surface-900 border-l-4 border-momap flex items-center justify-between">
                <div class="space-y-1">
                  <h4 class="font-medium text-lg flex items-center gap-2">
                    <span class="material-symbols-outlined text-momap">admin_panel_settings</span>
                    Modalità Amministratore
                  </h4>
                  <p class="text-sm text-surface-500 dark:text-surface-400">
                    Abilita tutti i permessi disponibili
                  </p>
                </div>
                <ToggleSwitch 
                  v-model="isAdmin" 
                  @change="handleAdminToggle"
                  class="scale-125"
                />
              </div>

              <!-- Categories Accordion -->
              <div class="space-y-2">
                <Accordion v-model:activeIndex="activeCategories" :multiple="true" class="custom-accordion">
                  <AccordionPanel v-for="(category, index) in permissionCategories" 
                                  :key="category.name"
                                  :value="index.toString()">
                    <AccordionHeader>
                      <div class="flex items-center justify-between w-full py-2">
                        <div class="flex items-center gap-3">
                          <span class="material-symbols-outlined text-momap">{{ category.icon }}</span>
                          <span class="font-medium">{{ category.label }}</span>
                        </div>
                        <div class="flex items-center">
                          <Badge :value="getSelectedCount(category.name)" 
                                 :severity="getSelectedCount(category.name) > 0 ? 'success' : 'info'" 
                                 class="badge-custom mr-8" />
                        </div>
                      </div>
                    </AccordionHeader>
                    <AccordionContent>
                      <div class="space-y-2 p-3">
                        <div v-for="permission in category.permissions" 
                             :key="permission.value"
                             class="permission-card"
                             :class="{ 'is-disabled': isAdmin }"
                             @click="!isAdmin && togglePermission(permission.value)">
                          <div class="permission-content">
                            <div class="flex items-start gap-4">
                              <div class="custom-checkbox mt-1" 
                                   :class="{ 
                                     'is-checked': selectedPermissions.includes(permission.value),
                                     'is-disabled': isAdmin 
                                   }">
                                <span v-if="selectedPermissions.includes(permission.value)" 
                                      class="material-symbols-outlined text-white text-sm">
                                  check
                                </span>
                              </div>
                              <div class="flex-1">
                                <div class="font-medium text-lg mb-2">{{ permission.label }}</div>
                                <p class="text-surface-600 dark:text-surface-400 mb-3">
                                  {{ permission.description }}
                                </p>
                                <div class="flex flex-wrap gap-2">
                                  <span v-for="endpoint in permission.endpoints" 
                                        :key="endpoint"
                                        class="endpoint-tag">
                                    {{ endpoint }}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionPanel>
                </Accordion>
              </div>
            </div>
          </div>

          <!-- Manual step -->
          <div v-else-if="currentStep === 3" key="manual" class="animate-fadeIn">
            <div class="space-y-6">
              <h3 class="text-xl font-semibold">Inserisci le credenziali esistenti</h3>
              <div class="space-y-4">
                <div class="form-group">
                  <label class="block mb-2 text-sm font-medium text-surface-600 dark:text-surface-400">
                    API Key
                  </label>
                  <InputText 
                    v-model="manualCredentials.apiKey"
                    class="w-full"
                    placeholder="Inserisci la tua API Key"
                  />
                </div>
                <div class="form-group">
                  <label class="block mb-2 text-sm font-medium text-surface-600 dark:text-surface-400">
                    API Secret
                  </label>
                  <Password
                    v-model="manualCredentials.apiSecret"
                    class="w-full"
                    :feedback="false"
                    toggleMask
                    placeholder="Inserisci il tuo API Secret"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Credentials step -->
          <div v-else-if="currentStep === 2" key="credentials" class="animate-fadeIn">
            <div class="space-y-6">
              <div class="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                <div class="flex items-center gap-2 text-green-700 dark:text-green-400 mb-2">
                  <span class="material-symbols-outlined">check_circle</span>
                  <span class="font-medium">Credenziali generate con successo!</span>
                </div>
                <p class="text-sm text-green-600 dark:text-green-500 mb-4">
                  Salva queste credenziali in un posto sicuro. Non saranno più visualizzabili dopo la chiusura.
                </p>
              </div>

              <!-- API Key -->
              <div class="space-y-2">
                <label class="text-sm font-medium text-surface-600 dark:text-surface-400">API Key</label>
                <div class="flex items-center gap-2">
                  <code class="flex-1 p-3 bg-surface-50 dark:bg-surface-900 rounded font-mono text-sm">
                    {{ generatedCredentials?.api_key }}
                  </code>
                  <Button 
                    severity="secondary" 
                    @click="copyToClipboard(generatedCredentials?.api_key)"
                    class="!p-3"
                  >
                    <span class="material-symbols-outlined">content_copy</span>
                  </Button>
                </div>
              </div>

              <!-- API Secret -->
              <div class="space-y-2">
                <label class="text-sm font-medium text-surface-600 dark:text-surface-400">API Secret</label>
                <div class="flex items-center gap-2">
                  <code class="flex-1 p-3 bg-surface-50 dark:bg-surface-900 rounded font-mono text-sm">
                    {{ generatedCredentials?.api_secret }}
                  </code>
                  <Button 
                    severity="secondary" 
                    @click="copyToClipboard(generatedCredentials?.api_secret)"
                    class="!p-3"
                  >
                    <span class="material-symbols-outlined">content_copy</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>

      <!-- Footer con pulsanti -->
      <div class="flex justify-between mt-6">
        <Button 
          v-if="currentStep > 0" 
          severity="secondary"
          @click="handleBack"
        >
          <div class="flex items-center gap-2">
            <span class="material-symbols-outlined">arrow_back</span>
            <span>Indietro</span>
          </div>
        </Button>
        
        <Button 
          v-if="currentStep === 1"
          :disabled="!hasSelectedPermissions"
          class="!bg-green-500 hover:!bg-green-600"
          :loading="generating"
          @click="handleNext"
        >
          <div class="flex items-center gap-2">
            <span>Continua</span>
            <span class="material-symbols-outlined">arrow_forward</span>
          </div>
        </Button>

        <Button 
          v-if="currentStep === 3"
          :disabled="!canSaveManual"
          class="!bg-green-500 hover:!bg-green-600 ml-auto"
          @click="saveManualCredentials"
        >
          <div class="flex items-center gap-2">
            <span>Salva</span>
            <span class="material-symbols-outlined">save</span>
          </div>
        </Button>

        <Button 
          v-if="currentStep === 2"
          class="!bg-green-500 hover:!bg-green-600 ml-auto"
          @click="complete"
        >
          <div class="flex items-center gap-2">
            <span>Chiudi</span>
            <span class="material-symbols-outlined">check</span>
          </div>
        </Button>
      </div>
    </div>
  </Dialog>
</div>
</template>

<script setup>
import { ref, computed, watch, inject } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useSetupCredentialsStore } from '@/stores/setupCredentials';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';


// HttpService
const HttpService = inject('HttpService');


const props = defineProps({
    visible: {
        type: Boolean,
        required: true
    }
});

const emit = defineEmits(['update:visible', 'complete']);

const toast = useToast();
const setupMode = ref('generate');
const currentStep = ref(0);
const generating = ref(false);
const generatedCredentials = ref(null);
const selectedPermissions = ref([]);
const manualCredentials = ref({
    apiKey: '',
    apiSecret: ''
});

const setupCredentialsStore = useSetupCredentialsStore();

const steps = computed(() => {
    return setupMode.value === 'generate' 
        ? [
            { label: 'Benvenuto' },
            { label: 'Permessi' },
            { label: 'Credenziali' }
        ]
        : [
            { label: 'Benvenuto' },
            { label: 'Inserimento' }
        ];
});

const emailPermissions = [
    {
        value: 'email:send',
        label: 'Invio Email',
        description: 'Permette di inviare email attraverso l\'API',
        endpoints: ['POST /api/email/send']
    },
    {
        value: 'email:read',
        label: 'Lettura Email',
        description: 'Permette di leggere lo stato e le informazioni delle email inviate',
        endpoints: ['GET /api/email/status/:messageId']
    },
    {
        value: 'email:blacklist:read',
        label: 'Lettura Blacklist',
        description: 'Permette di visualizzare la blacklist',
        endpoints: ['GET /api/email/blacklist']
    },
    {
        value: 'email:blacklist:write',
        label: 'Gestione Blacklist',
        description: 'Permette di gestire la blacklist (aggiungere/rimuovere email)',
        endpoints: ['POST /api/email/blacklist', 'DELETE /api/email/blacklist/:email']
    }
];

const analyticsPermissions = [
    {
        value: 'analytics:read',
        label: 'Statistiche Base',
        description: 'Permette di accedere alle statistiche base',
        endpoints: ['GET /api/analytics/stats', 'GET /api/analytics/events']
    },
    {
        value: 'analytics:advanced',
        label: 'Statistiche Avanzate',
        description: 'Permette di accedere alle statistiche avanzate e report',
        endpoints: ['GET /api/analytics/report', 'GET /api/analytics/events-dashboard']
    }
];

const webhookPermissions = [
    {
        value: 'webhook:read',
        label: 'Lettura Webhook',
        description: 'Permette di visualizzare i webhook configurati',
        endpoints: ['GET /api/webhooks', 'GET /api/webhooks/notification-queue']
    },
    {
        value: 'webhook:write',
        label: 'Gestione Webhook',
        description: 'Permette di gestire i webhook (aggiungere/rimuovere)',
        endpoints: ['POST /api/webhooks/register', 'DELETE /api/webhooks/:event']
    }
];

const userPermissions = [
    {
        value: 'users:read',
        label: 'Lettura Utenti',
        description: 'Permette di visualizzare informazioni sugli utenti',
        endpoints: ['GET /api/users/:id', 'GET /api/users/:id/stats']
    },
    {
        value: 'users:write',
        label: 'Gestione Utenti',
        description: 'Permette di gestire gli utenti (creare/modificare/eliminare)',
        endpoints: ['POST /api/users', 'PUT /api/users/:id', 'DELETE /api/users/:id']
    }
];

const systemPermissions = [
    {
        value: 'admin',
        label: 'Amministratore',
        description: 'Accesso completo a tutte le funzionalità (include tutti i permessi)',
        endpoints: ['Tutti gli endpoint']
    },
    {
        value: 'system:test',
        label: 'Test Sistema',
        description: 'Permette di eseguire operazioni di test',
        endpoints: ['POST /api/email/test-tracking', 'POST /api/webhooks/test-notification']
    }
];

const isAdmin = ref(false);
const activeCategories = ref([0]);
const previousPermissions = ref([]);

// Modifica la gestione dell'admin toggle
const handleAdminToggle = (event) => {
    const value = event.target.checked;
    isAdmin.value = value;
    
    if (value) {
        previousPermissions.value = [...selectedPermissions.value];
        selectedPermissions.value = ['admin'];
    } else {
        selectedPermissions.value = previousPermissions.value.length ? 
            [...previousPermissions.value] : [];
        previousPermissions.value = [];
        activeCategories.value = [0];
    }
};

const togglePermission = (permission) => {
    if (isAdmin.value) return;
    
    const index = selectedPermissions.value.indexOf(permission);
    if (index === -1) {
        selectedPermissions.value.push(permission);
    } else {
        selectedPermissions.value.splice(index, 1);
    }
};

// Rimuovi il watch problematico su selectedPermissions

// Conta i permessi selezionati per categoria
const getSelectedCount = (category) => {
    const categoryPermissions = {
        email: emailPermissions,
        analytics: analyticsPermissions,
        webhook: webhookPermissions,
        users: userPermissions,
        system: systemPermissions
    };
    
    if (isAdmin.value) return categoryPermissions[category].length;
    
    return selectedPermissions.value.filter(p => 
        categoryPermissions[category].some(cp => cp.value === p)
    ).length;
};

const canProceed = computed(() => {
    if (currentStep.value === 1) {
        return selectedPermissions.value.length > 0;
    }
    return true;
});

const canSaveManual = computed(() => {
    return manualCredentials.value.apiKey && manualCredentials.value.apiSecret;
});

const handleNext = async () => {
    if (currentStep.value === 1) {
        await generateCredentials();
    } else {
        currentStep.value++;
    }
};

const generateCredentials = async () => {
    if (!selectedPermissions.value.length) {
        toast.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Seleziona almeno un permesso',
            life: 3000
        });
        return;
    }

    generating.value = true;
    try {
        // Ottengo l'userId dal token JWT nei cookie
        const token = Cookies.get('exampleJwt', { domain: import.meta.env.VITE_BASE_URL_COOKIES });
        const decodedToken = jwtDecode(token);
        const userId = decodedToken.id;

        const response = await HttpService.post('/credentials/generate', {
            userId: userId,
            permissions: selectedPermissions.value
        });

        // Mostro le nuove credenziali
        const newCredentials = response.data;
        toast.add({
            severity: 'success',
            summary: 'Credenziali Generate',
            detail: 'Salva subito API Key e Secret!'
        });

        // Mostro un dialog con le credenziali
        generatedCredentials.value = newCredentials.credentials;
        currentStep.value = 2;
    } catch (error) {
        console.error('Error generating API credentials:', error);
        toast.add({
            severity: 'error',
            summary: 'Errore',
            detail: error.response?.data?.message || 'Impossibile generare le credenziali API',
            life: 3000
        });
    } finally {
        generating.value = false;
    }
};

const saveManualCredentials = () => {
    emit('complete', {
        api_key: manualCredentials.value.apiKey,
        api_secret: manualCredentials.value.apiSecret
    });
    emit('update:visible', false);
};

const complete = () => {
    setupCredentialsStore.completeSetup();
    emit('complete', generatedCredentials.value);
    emit('update:visible', false);
    //reload page
    window.location.reload();
};

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        toast.add({
            severity: 'success',
            summary: 'Copiato',
            detail: 'Testo copiato negli appunti',
            life: 2000
        });
    } catch (err) {
        toast.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile copiare il testo',
            life: 3000
        });
    }
};

// Aggiungi questo watch per gestire la chiusura degli accordion quando viene selezionato admin
watch(isAdmin, (newValue) => {
    if (newValue) {
        activeCategories.value = [];
    }
});

const handleClose = () => {
  // Rimuoviamo il controllo che impedisce la chiusura se il setup non è completo
  // così che il modale possa essere chiuso in ogni caso
  emit('update:visible', false);
};

const handleBack = () => {
    currentStep.value = 0;
    manualCredentials.value = {
        apiKey: '',
        apiSecret: ''
    };
};

const hasSelectedPermissions = computed(() => {
    return selectedPermissions.value.length > 0;
});

const permissionCategories = [
    { 
        name: 'email', 
        label: 'Email', 
        icon: 'mail',
        permissions: emailPermissions 
    },
    {
        name: 'analytics',
        label: 'Analytics',
        icon: 'analytics',
        permissions: analyticsPermissions
    },
    {
        name: 'webhook',
        label: 'Webhook',
        icon: 'webhook',
        permissions: webhookPermissions
    },
    {
        name: 'users',
        label: 'Utenti',
        icon: 'group',
        permissions: userPermissions
    },
    {
        name: 'system',
        label: 'Sistema',
        icon: 'settings',
        permissions: systemPermissions
    }
];
</script>

<style scoped>
.api-setup-modal {
  @apply !rounded-none;
}

.api-setup-modal :deep(.p-dialog-header) {
  @apply !p-8 !pb-16 border-b border-surface-200 dark:border-surface-700;
}

.api-setup-modal :deep(.p-dialog-content) {
  @apply !p-8;
}

/* Steps customization */
.custom-steps :deep(.p-steps) {
  @apply !bg-transparent;
}

.custom-steps :deep(.p-steps-item) {
  @apply flex-1;
}

.custom-steps :deep(.p-steps-number) {
  @apply !bg-surface-200 dark:!bg-surface-700 !text-surface-600 dark:!text-surface-300 
         !w-8 !h-8 !leading-8 !text-sm;
}

.custom-steps :deep(.p-steps-title) {
  @apply !text-sm whitespace-nowrap;
}

.custom-steps :deep(.p-steps-item.p-highlight .p-steps-number) {
  @apply !bg-momap !text-white;
}

/* Accordion customization */
.custom-accordion :deep(.p-accordion-tab) {
  @apply mb-2;
}

.custom-accordion :deep(.p-accordion-header-link) {
  @apply !p-4 !bg-white dark:!bg-surface-800 !border !border-surface-200 
         dark:!border-surface-700 !rounded-lg !mb-2
         hover:!bg-surface-50 dark:hover:!bg-surface-700;
}

.custom-accordion :deep(.p-accordion-header-link > span:last-child) {
  @apply !mr-4;
}

.custom-accordion :deep(.p-accordion-content) {
  @apply !p-0 !border !border-surface-200 dark:!border-surface-700 !rounded-lg !mb-2;
}

.custom-accordion :deep(.p-accordion-header) {
  @apply !bg-transparent;
}

/* Permission item styling */
.permission-item {
  @apply bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 
         transition-all duration-200;
}

.permission-item:not(.is-disabled) {
  @apply cursor-pointer;
}

.permission-item:hover:not(.is-disabled) {
  @apply bg-surface-50 dark:bg-surface-700;
}

.is-disabled {
  @apply opacity-60;
}

:deep(.p-checkbox-box) {
  @apply transition-all duration-200;
}

:deep(.p-checkbox-box:not([disabled])) {
  @apply cursor-pointer;
}

/* Checkbox customization */
:deep(.p-checkbox) {
  @apply !rounded-none;
}

:deep(.p-checkbox-box) {
  @apply !rounded-none !border-surface-300 dark:!border-surface-600 
         transition-colors duration-200;
}

:deep(.p-checkbox-box.p-highlight) {
  @apply !bg-momap !border-momap;
}

:deep(.p-checkbox-box:not(.p-disabled):hover) {
  @apply !border-momap/50;
}

/* Animations */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease-out;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.animate-fadeIn {
  animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.permission-card {
  @apply bg-white dark:bg-surface-800 rounded-lg border border-surface-200 dark:border-surface-700
         transition-all duration-200;
}

.permission-card:not(.is-disabled) {
  @apply hover:shadow-md hover:border-momap/30 hover:-translate-y-0.5;
}

.permission-card.is-disabled {
  @apply opacity-60 cursor-not-allowed;
}

.permission-content {
  @apply p-4;
}

.endpoint-tag {
  @apply px-2 py-1 text-xs font-mono bg-surface-50 dark:bg-surface-900
         text-surface-600 dark:text-surface-400 border border-surface-200 
         dark:border-surface-700 rounded;
}

.custom-accordion :deep(.p-accordion-header-link) {
  @apply !p-4 !bg-white dark:!bg-surface-800 !border !border-surface-200 
         dark:!border-surface-700 !rounded-lg !mb-2
         hover:!bg-surface-50 dark:hover:!bg-surface-700;
}

.custom-accordion :deep(.p-accordion-content) {
  @apply !p-0 !border !border-surface-200 dark:!border-surface-700 !rounded-lg !mb-2;
}

.custom-checkbox {
  @apply w-5 h-5 flex items-center justify-center rounded
         border-2 border-surface-300 dark:border-surface-600
         transition-all duration-200 bg-white dark:bg-surface-800;
}

.custom-checkbox.is-checked {
  @apply bg-momap border-momap;
}

.custom-checkbox.is-checked.is-disabled {
  @apply bg-surface-400 dark:bg-surface-600 border-surface-400 dark:border-surface-600;
}

.custom-checkbox:not(.is-disabled) {
  @apply cursor-pointer hover:border-momap;
}

/* Migliora lo stile del badge */
:deep(.badge-custom) {
  @apply !rounded-full !w-6 !h-6 !p-0 flex items-center justify-center !min-w-0;
}

:deep(.badge-custom.p-badge) {
  @apply !text-xs !font-medium;
}

:deep(.badge-custom.p-badge-success) {
  @apply !bg-green-500;
}

:deep(.badge-custom.p-badge-info) {
  @apply !bg-surface-500 dark:!bg-surface-600;
}

</style> 